import { useEffect, useRef, useState } from "react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import {
  AlertTriangle,
  BookTemplate,
  ChevronDown,
  ChevronUp,
  Copy,
  Edit,
  FileText,
  List,
  Lock,
  Pencil,
  Plus,
  Send,
  CheckCircle,
  XCircle,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import {
  buildImportRows,
  oscUntrusted,
  pickInsertable,
  summarizeImport,
  type ZERoadNeighborsInfo,
} from "./zeImportParams";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useAuthStore } from "@/store/auth";
import type {
  PricingItemTemplate,
  PricingPreset,
  ResolvedPresetItem,
  TenderPricingProposal,
  TenderRound,
} from "../types";
import { SimilarProposals } from "./SimilarProposals";
import { PricingHint } from "./PricingHint";
import {
  useCreatePricingProposal,
  usePricingPresets,
  usePricingTemplates,
  useResolvePricingPreset,
  useReviewPricingProposal,
  useSendPricingNotification,
  useSubmitTenderOffer,
  useWithdrawTenderOffer,
  useUpdatePricingProposal,
} from "../hooks/useTenders";
import { PRICING_PROPOSAL_STATUS_LABELS } from "../types";
import apiClient from "@/api/client";
import type { AuthUser } from "@/store/auth";

const ROUND_STATUS_COLORS: Record<string, "blue" | "green" | "gray"> = {
  active: "blue",
  submitted: "green",
  completed: "gray",
};

const ROUND_STATUS_LABELS: Record<string, string> = {
  active: "Aktywna",
  submitted: "Złożona oferta",
  completed: "Zakończona",
};

const PROPOSAL_STATUS_COLORS: Record<string, "yellow" | "green" | "red"> = {
  pending: "yellow",
  accepted: "green",
  rejected: "red",
};

interface PricingItem {
  key: string;
  name: string;
  base_rate: string;
  quantity: string;
  /** Source catalog template ID — snapshotted price, used for traceability only. */
  template_id?: string | null;
  /** False — item is counted 1:1, without the pricing multiplier. */
  apply_multiplier: boolean;
}

function emptyItem(): PricingItem {
  return { key: crypto.randomUUID(), name: "", base_rate: "", quantity: "", template_id: null, apply_multiplier: true };
}

/**
 * A line item counts as "zero" (greyed out and sorted to the end) when its
 * quantity is explicitly set and resolves to 0. An empty/unset quantity is not
 * treated as zero, so freshly added rows stay where they are.
 */
function isZeroQuantity(quantity: string): boolean {
  const trimmed = quantity.trim();
  if (trimmed === "") return false;
  return (parseFloat(trimmed.replace(",", ".")) || 0) === 0;
}

/** Stable comparator pushing zero-quantity items to the end, preserving order otherwise. */
function byZeroQuantityLast(a: { quantity: string }, b: { quantity: string }): number {
  return Number(isZeroQuantity(a.quantity)) - Number(isZeroQuantity(b.quantity));
}

function fmt(n: number): string {
  const [int, dec] = Math.abs(n).toFixed(2).split(".");
  const intFormatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
  return (n < 0 ? "-" : "") + intFormatted + "," + dec;
}

interface ZEVariantOption {
  id: string;
  label: string;
  /** MPZP coverage summary (from ZETenderWorkspacePage) — surfaced after a ZE import
   *  so the pricing author sees the same gap warnings as "Działki pod trasą". */
  mpzpCoveragePct?: number | null;
  mpzpUnknownPct?: number | null;
  mpzpComputedAt?: string | null;
  hasMpzpGap?: boolean;
  /** Route cuts at least one GDOŚ protected area (rezerwat / park / Natura 2000 …). */
  hasProtectedAreas?: boolean;
}

/** Structural shape of the ZE quantity-summary, declared here rather than imported so
 *  features/tenders keeps no hard dependency on features/ze_tenders. `QuantitySummary`
 *  is assignable to it. */
export interface ZEImportSummary {
  preset_params?: Record<string, number>;
  lines: unknown[];
  objects: unknown[];
  road_neighbors?: ZERoadNeighborsInfo;
}

interface Props {
  tenderId: string;
  round: TenderRound;
  previousRound?: TenderRound;
  defaultOpen?: boolean;
  /** ZE drawing variants linked to this tender (from ZETenderWorkspacePage). */
  zeVariants?: ZEVariantOption[];
  /** Fetches the drawing's quantity summary for a variant — provided by ZETenderWorkspacePage. */
  onLoadZESummary?: (variantId: string) => Promise<ZEImportSummary>;
  /** Runs the internal-road neighbour sweep for a variant, so the pricing author can fix
   *  untrustworthy osc keys without leaving this screen. */
  onRecomputeRoadNeighbors?: (variantId: string) => Promise<void>;
  /** A sweep is pending/running (shared with the parcels panel). */
  roadNeighborsBusy?: boolean;
}

export function TenderRoundSection({ tenderId, round, previousRound, defaultOpen = false, zeVariants, onLoadZESummary, onRecomputeRoadNeighbors, roadNeighborsBusy }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const user = useAuthStore((s) => s.user);
  const canIssueOffers = !!user?.has_issue_offers_permission;
  const canCalculatePricing = !!user?.has_calculate_pricing_permission;
  const canApprovePricing = !!user?.has_approve_pricing_permission;

  // ── Offer form ────────────────────────────────────────────────────────────
  const [offerModal, setOfferModal] = useState(false);
  const [offerValue, setOfferValue] = useState("");
  const [offerNotes, setOfferNotes] = useState("");
  const [offerFile, setOfferFile] = useState<File | null>(null);

  // ── Pricing form ──────────────────────────────────────────────────────────
  const [pricingModal, setPricingModal] = useState(false);
  const [editingProposalId, setEditingProposalId] = useState<string | null>(null);
  const [pricingItems, setPricingItems] = useState<PricingItem[]>([emptyItem()]);
  const [pricingMultiplier, setPricingMultiplier] = useState(100);
  // Raw text of the multiplier input while the user is typing — committed on blur
  // so per-keystroke clamping doesn't rewrite partial values like "2" (of "250").
  const [multiplierDraft, setMultiplierDraft] = useState<string | null>(null);
  const [pricingNotes, setPricingNotes] = useState("");

  // ── Catalog / preset mode ─────────────────────────────────────────────────
  type CatalogMode = "preset" | "catalog" | "manual";
  const [catalogMode, setCatalogMode] = useState<CatalogMode>("preset");
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [presetParamValues, setPresetParamValues] = useState<Record<string, string>>({});
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSelected, setCatalogSelected] = useState<Set<string>>(new Set());

  // ── ZE drawing import ────────────────────────────────────────────────────
  const hasZE = !!zeVariants && zeVariants.length > 0 && !!onLoadZESummary;
  const [zeImportVariantId, setZeImportVariantId] = useState<string>(() =>
    zeVariants && zeVariants.length > 0 ? zeVariants[0].id : ""
  );
  const [zeImportLoading, setZeImportLoading] = useState(false);
  const [zeImport, setZeImport] = useState<ZEImportSummary | null>(null);
  const [zeImportError, setZeImportError] = useState("");
  const zeImportParams = zeImport?.preset_params ?? null;

  const loadZEParams = async (variantId: string) => {
    if (!onLoadZESummary || !variantId) return;
    setZeImportLoading(true);
    setZeImportError("");
    try {
      setZeImport(await onLoadZESummary(variantId));
    } catch {
      setZeImportError("Nie udało się wczytać danych z rysunku.");
    } finally {
      setZeImportLoading(false);
    }
  };

  // A finished sweep changes the osc keys, so re-read the summary in place instead of
  // leaving the author looking at numbers the recompute just invalidated.
  const prevRoadNeighborsBusy = useRef(false);
  useEffect(() => {
    if (prevRoadNeighborsBusy.current && !roadNeighborsBusy && zeImport)
      void loadZEParams(zeImportVariantId || (zeVariants?.[0]?.id ?? ""));
    prevRoadNeighborsBusy.current = !!roadNeighborsBusy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadNeighborsBusy]);

  // ── Copy picker ───────────────────────────────────────────────────────────
  const [copyPickerModal, setCopyPickerModal] = useState(false);

  // ── Click-to-copy proposal value ──────────────────────────────────────────
  const [copiedProposalId, setCopiedProposalId] = useState<string | null>(null);

  // Copies the raw amount ("10250,00" — comma decimal, no grouping) so it can
  // be pasted straight into money inputs, then flashes a "Skopiowano" bubble.
  const copyProposalValue = (proposalId: string, value: string) => {
    const raw = Number(value).toFixed(2).replace(".", ",");
    navigator.clipboard.writeText(raw).then(() => {
      setCopiedProposalId(proposalId);
      setTimeout(
        () => setCopiedProposalId((cur) => (cur === proposalId ? null : cur)),
        1500
      );
    });
  };

  // ── Notify form ───────────────────────────────────────────────────────────
  const [notifyModal, setNotifyModal] = useState(false);
  const [notifyProposalId, setNotifyProposalId] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyRecipients, setNotifyRecipients] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<{ id: string; first_name: string; last_name: string }[]>([]);

  const submitOffer = useSubmitTenderOffer(tenderId);
  const withdrawOffer = useWithdrawTenderOffer(tenderId);
  const createProposal = useCreatePricingProposal(tenderId);
  const updateProposal = useUpdatePricingProposal(tenderId);
  const sendNotification = useSendPricingNotification(tenderId);
  const reviewProposal = useReviewPricingProposal(tenderId);
  const resolvePreset = useResolvePricingPreset();

  // Catalog data (active only, no client filter since prop not passed)
  const { data: allPresets = [] } = usePricingPresets();
  const { data: allTemplates = [] } = usePricingTemplates();

  // ── Computed totals ───────────────────────────────────────────────────────
  // Items with apply_multiplier=false are counted 1:1, outside the multiplier.
  const lineTotalOf = (item: PricingItem) => {
    const rate = parseFloat(item.base_rate.replace(",", ".")) || 0;
    const qty = parseFloat(item.quantity.replace(",", ".")) || 0;
    return rate * qty;
  };
  const subtotal = pricingItems.reduce((sum, item) => sum + lineTotalOf(item), 0);
  const exemptSubtotal = pricingItems.reduce(
    (sum, item) => (item.apply_multiplier ? sum : sum + lineTotalOf(item)),
    0
  );
  const multipliedSubtotal = subtotal - exemptSubtotal;
  const total = multipliedSubtotal * pricingMultiplier / 100 + exemptSubtotal;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const loadUsers = async () => {
    const res = await apiClient.get<{ data: AuthUser[] }>("/auth/internal-users/");
    setAllUsers((res.data.data || []).map((u) => ({ id: u.id, first_name: u.first_name, last_name: u.last_name })));
  };

  const openCreatePricing = () => {
    setEditingProposalId(null);
    setPricingItems([emptyItem()]);
    setPricingMultiplier(100);
    setPricingNotes("");
    setCatalogMode("preset");
    setSelectedPresetId("");
    setPresetParamValues({});
    setCatalogSearch("");
    setCatalogSelected(new Set());
    setPricingModal(true);
  };

  const openEditPricing = (p: TenderPricingProposal) => {
    // Always create a NEW proposal pre-filled with old values — old proposal stays intact.
    setEditingProposalId(null);
    setPricingItems(
      p.items.length > 0
        ? p.items.map((it) => ({
            key: crypto.randomUUID(),
            name: it.name,
            base_rate: it.base_rate,
            quantity: it.quantity,
            template_id: it.source_template,
            apply_multiplier: it.apply_multiplier ?? true,
          }))
        : [emptyItem()]
    );
    setPricingMultiplier(parseFloat(p.multiplier));
    setPricingNotes(p.notes);
    setCatalogMode("manual");
    setSelectedPresetId("");
    setPresetParamValues({});
    setCatalogSearch("");
    setCatalogSelected(new Set());
    setPricingModal(true);
  };

  const openCopyFromPrevious = () => {
    if (!previousRound || previousRound.pricing_proposals.length === 0) return;
    // Prefer accepted proposal, otherwise take latest created
    const source =
      previousRound.pricing_proposals.find((p) => p.status === "accepted") ??
      [...previousRound.pricing_proposals].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    setEditingProposalId(null);
    setPricingItems(
      source.items.length > 0
        ? source.items.map((it) => ({
            key: crypto.randomUUID(),
            name: it.name,
            base_rate: it.base_rate,
            quantity: it.quantity,
            template_id: it.source_template,
            apply_multiplier: it.apply_multiplier ?? true,
          }))
        : [emptyItem()]
    );
    setPricingMultiplier(parseFloat(source.multiplier));
    setPricingNotes(source.notes);
    setCatalogMode("manual");
    setSelectedPresetId("");
    setPresetParamValues({});
    setCatalogSearch("");
    setCatalogSelected(new Set());
    setPricingModal(true);
  };

  const openCopyFromProposal = (p: TenderPricingProposal) => {
    setEditingProposalId(null);
    setPricingItems(
      p.items.length > 0
        ? p.items.map((it) => ({
            key: crypto.randomUUID(),
            name: it.name,
            base_rate: it.base_rate,
            quantity: it.quantity,
            template_id: it.source_template,
            apply_multiplier: it.apply_multiplier ?? true,
          }))
        : [emptyItem()]
    );
    setPricingMultiplier(parseFloat(p.multiplier));
    setPricingNotes(p.notes);
    setCatalogMode("manual");
    setSelectedPresetId("");
    setPresetParamValues({});
    setCatalogSearch("");
    setCatalogSelected(new Set());
    setCopyPickerModal(false);
    setPricingModal(true);
  };

  const handleCopyFromExisting = () => {
    if (round.pricing_proposals.length === 0) return;
    if (round.pricing_proposals.length === 1) {
      openCopyFromProposal(round.pricing_proposals[0]);
    } else {
      setCopyPickerModal(true);
    }
  };

  const handleSubmitOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitOffer.mutateAsync({ roundId: round.id, data: { value: offerValue, notes: offerNotes, file: offerFile } });
    setOfferModal(false);
    setOfferValue(""); setOfferNotes(""); setOfferFile(null);
  };

  const handleSavePricing = async (e: React.FormEvent) => {
    e.preventDefault();
    const items = pricingItems
      .filter((i) => i.name.trim() || i.base_rate || i.quantity)
      .map((i) => ({
        name: i.name,
        base_rate: i.base_rate.replace(",", "."),
        quantity: i.quantity.replace(",", "."),
        template_id: i.template_id ?? null,
        apply_multiplier: i.apply_multiplier,
      }));
    const data = { notes: pricingNotes, multiplier: pricingMultiplier, items, value: String(total.toFixed(2)), ze_variant_id: zeImportVariantId || null };

    if (editingProposalId) {
      await updateProposal.mutateAsync({ roundId: round.id, proposalId: editingProposalId, data });
    } else {
      await createProposal.mutateAsync({ roundId: round.id, data });
    }
    setPricingModal(false);
  };

  const updateItem = (key: string, field: keyof Omit<PricingItem, "key">, value: string) => {
    setPricingItems((prev) => prev.map((it) => it.key === key ? { ...it, [field]: value } : it));
  };

  const toggleItemMultiplier = (key: string) => {
    setPricingItems((prev) =>
      prev.map((it) => it.key === key ? { ...it, apply_multiplier: !it.apply_multiplier } : it)
    );
  };

  const removeItem = (key: string) => {
    setPricingItems((prev) => prev.length > 1 ? prev.filter((it) => it.key !== key) : prev);
  };

  const openNotifyModal = (proposalId: string) => {
    setNotifyProposalId(proposalId);
    setNotifyRecipients([]); setNotifyMessage("");
    setNotifyModal(true);
    loadUsers();
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendNotification.mutateAsync({ roundId: round.id, proposalId: notifyProposalId, recipient_ids: notifyRecipients, message: notifyMessage });
    setNotifyModal(false);
  };

  const toggleRecipient = (uid: string) => {
    setNotifyRecipients((prev) => prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]);
  };

  // Handle preset parameter change
  const handlePresetParamChange = (key: string, value: string) => {
    setPresetParamValues((prev) => ({ ...prev, [key]: value }));
  };

  // Apply preset — resolves items and replaces/populates the items table
  const handleApplyPreset = async () => {
    if (!selectedPresetId) return;
    const hasItems = pricingItems.some((i) => i.name.trim() || i.base_rate || i.quantity);
    if (hasItems && !window.confirm("Zastąpić istniejące pozycje pozycjami z presetu?")) return;

    const params: Record<string, number> = {};
    Object.entries(presetParamValues).forEach(([k, v]) => {
      params[k] = parseFloat(v) || 0;
    });

    const resolved = await resolvePreset.mutateAsync({ id: selectedPresetId, parameters: params });
    const newItems: PricingItem[] = resolved.map((r: ResolvedPresetItem) => ({
      key: crypto.randomUUID(),
      name: r.name,
      base_rate: r.base_rate,
      quantity: r.quantity ?? "",
      template_id: r.template_id,
      apply_multiplier: r.apply_multiplier ?? true,
    }));
    if (newItems.length === 0) {
      newItems.push(emptyItem());
    }
    setPricingItems(newItems);
  };

  // Add selected catalog items (append)
  const handleAddFromCatalog = () => {
    if (catalogSelected.size === 0) return;
    const toAdd: PricingItem[] = allTemplates
      .filter((t: PricingItemTemplate) => catalogSelected.has(t.id))
      .map((t: PricingItemTemplate) => ({
        key: crypto.randomUUID(),
        name: t.name,
        base_rate: t.base_rate,
        quantity: "1",
        template_id: t.id,
        apply_multiplier: t.apply_multiplier ?? true,
      }));
    // Remove the placeholder empty item if present
    const existing = pricingItems.filter((i) => i.name.trim() || i.base_rate || i.quantity);
    setPricingItems([...existing, ...toAdd]);
    setCatalogSelected(new Set());
  };

  const toggleCatalogItem = (id: string) => {
    setCatalogSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isPricingPending = createProposal.isPending || updateProposal.isPending;
  const canSavePricing = pricingItems.some((i) => i.name.trim() && i.base_rate && i.quantity);

  return (
    <div className="rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Tura {round.round_number}</span>
          <Badge color={ROUND_STATUS_COLORS[round.status]}>{ROUND_STATUS_LABELS[round.status]}</Badge>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Termin: {new Date(round.effective_deadline).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-400">
          <span className="text-xs">{round.offers.length > 0 ? "Oferta złożona" : "Brak oferty"}, {round.pricing_proposals.length} wycen</span>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-5 bg-white dark:bg-gray-800">
          {/* Actions */}
          {(() => {
            const activeOffer = round.offers.find((o) => !o.is_withdrawn);
            return (
              <div className="flex gap-2 flex-wrap">
                {canIssueOffers && !activeOffer && (
                  <Button size="sm" onClick={() => {
                    const accepted = round.pricing_proposals
                      .filter((p) => p.status === "accepted")
                      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                    setOfferValue(accepted ? accepted.value : "");
                    setOfferModal(true);
                  }}>
                    <Plus size={13} /> Oznacz jako złożona w przetargu
                  </Button>
                )}
                {canIssueOffers && activeOffer && (
                  <>
                    <Button size="sm" disabled variant="secondary">
                      <Plus size={13} /> Oznacz jako złożona w przetargu
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => withdrawOffer.mutate({ roundId: round.id, offerId: activeOffer.id })}
                      loading={withdrawOffer.isPending}
                    >
                      Wycofaj złożenie
                    </Button>
                  </>
                )}
              </div>
            );
          })()}
          {canCalculatePricing && (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="secondary" onClick={openCreatePricing}>
                <Plus size={13} /> Nowy wariant
              </Button>
              {round.pricing_proposals.length > 0 && (
                <Button size="sm" variant="secondary" onClick={handleCopyFromExisting}>
                  <Copy size={13} /> Nowy wariant (na podstawie istniejącego)
                </Button>
              )}
              {previousRound && previousRound.pricing_proposals.length > 0 && (
                <Button size="sm" variant="secondary" onClick={openCopyFromPrevious}>
                  <Edit size={13} /> Edytuj wycenę z tury {previousRound.round_number}
                </Button>
              )}
            </div>
          )}

          {/* Offers */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Złożona oferta</h4>
            {round.offers.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">Nie złożono jeszcze oferty.</p>
            ) : (
              <div className="space-y-2">
                {round.offers.map((offer) => (
                  <div key={offer.id} className={`flex items-center justify-between p-3 rounded-lg ${offer.is_withdrawn ? "bg-gray-100 dark:bg-gray-800/50 opacity-60" : "bg-gray-50 dark:bg-gray-700/50"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold font-mono ${offer.is_withdrawn ? "line-through text-gray-400" : "text-gray-900 dark:text-gray-100"}`}>
                          {fmt(Number(offer.value))} PLN
                        </span>
                        {offer.is_withdrawn && (
                          <span className="text-xs font-medium text-red-500 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">Wycofana</span>
                        )}
                        {!offer.is_withdrawn && (
                          <span className="text-xs font-medium text-green-600 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">Złożona</span>
                        )}
                      </div>
                      {offer.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{offer.notes}</p>}
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {offer.submitted_by_name} · {new Date(offer.created_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {offer.file_url && (
                        <a href={offer.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline text-xs">
                          <FileText size={13} /> Pobierz
                        </a>
                      )}
                      {canIssueOffers && (
                        <button
                          onClick={() => withdrawOffer.mutate({ roundId: round.id, offerId: offer.id })}
                          className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                          title={offer.is_withdrawn ? "Przywróć ofertę" : "Wycofaj ofertę"}
                        >
                          {offer.is_withdrawn ? "Przywróć" : "Wycofaj"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pricing Proposals */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Propozycje wyceny ({round.pricing_proposals.length})
            </h4>
            {round.pricing_proposals.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">Brak propozycji wyceny.</p>
            ) : (
              <div className="space-y-3">
                {round.pricing_proposals.map((p) => (
                  <div key={p.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="relative">
                            <button
                              type="button"
                              onClick={() => copyProposalValue(p.id, p.value)}
                              className="font-semibold text-gray-900 dark:text-gray-100 font-mono cursor-pointer hover:text-[var(--color-primary)] dark:hover:text-blue-300 transition-colors"
                              title="Kliknij, aby skopiować kwotę"
                            >
                              {fmt(Number(p.value))} PLN
                            </button>
                            {copiedProposalId === p.id && (
                              <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-[11px] px-2 py-0.5 rounded shadow-md pointer-events-none z-10">
                                Skopiowano
                              </span>
                            )}
                          </span>
                          <Badge color={PROPOSAL_STATUS_COLORS[p.status]}>
                            {PRICING_PROPOSAL_STATUS_LABELS[p.status as keyof typeof PRICING_PROPOSAL_STATUS_LABELS]}
                          </Badge>
                          {p.items.length > 0 && parseFloat(p.multiplier) !== 100 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({fmt(Number(p.subtotal))} PLN × {parseFloat(p.multiplier)}%)
                            </span>
                          )}
                        </div>

                        {/* Line items table */}
                        {p.items.length > 0 && (
                          <div className="mt-2 mb-2 overflow-x-auto">
                            <table className="w-full text-[10px] sm:text-xs border-collapse">
                              <thead>
                                <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">
                                  <th className="text-left py-1 pr-3 font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                                  <th className="text-right py-1 pr-3 font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka bazowa</th>
                                  <th className="text-right py-1 pr-3 font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
                                  <th className="text-right py-1 font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Razem</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...p.items].sort(byZeroQuantityLast).map((item) => (
                                  <tr
                                    key={item.id}
                                    className={`border-b border-gray-100 dark:border-gray-700/50${isZeroQuantity(item.quantity) ? " opacity-50" : ""}`}
                                  >
                                    <td className="py-1 pr-3 text-gray-700 dark:text-gray-300">
                                      <span className="inline-flex items-center gap-1">
                                        {item.name}
                                        {item.apply_multiplier === false && (
                                          <span
                                            title="Nie podlega współczynnikowi mnożnikowemu — liczona 1:1"
                                            className="cursor-help text-amber-500"
                                          >
                                            <Lock className="w-3 h-3" />
                                          </span>
                                        )}
                                      </span>
                                    </td>
                                    <td className="py-1 pr-3 text-right font-mono text-gray-700 dark:text-gray-300">
                                      <span className="inline-flex items-center gap-1">
                                        {fmt(Number(item.base_rate))}
                                        {item.rate_changed && (
                                          <span
                                            title={`Stawka bazowa szablonu zmieniła się: ${fmt(Number(item.base_rate))} → ${fmt(Number(item.current_rate))} PLN`}
                                            className="cursor-help"
                                          >
                                            <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                                          </span>
                                        )}
                                      </span>
                                    </td>
                                    <td className="py-1 pr-3 text-right font-mono text-gray-700 dark:text-gray-300">
                                      {Number(item.quantity).toLocaleString("pl-PL", { maximumFractionDigits: 3 })}
                                    </td>
                                    <td className="py-1 text-right font-mono text-gray-700 dark:text-gray-300">
                                      {fmt(Number(item.line_total))}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={3} className="pt-1.5 pr-3 text-right text-gray-500 dark:text-gray-400 font-medium">
                                    Suma bazowa:
                                  </td>
                                  <td className="pt-1.5 text-right font-mono font-semibold text-gray-900 dark:text-gray-100">
                                    {fmt(Number(p.subtotal))}
                                  </td>
                                </tr>
                                {parseFloat(p.multiplier) !== 100 && (() => {
                                  const exemptSum = p.items.reduce(
                                    (s, it) => (it.apply_multiplier === false ? s + Number(it.line_total) : s),
                                    0
                                  );
                                  return (
                                    <>
                                      {exemptSum > 0 && (
                                        <tr>
                                          <td colSpan={3} className="pr-3 text-right text-gray-500 dark:text-gray-400">
                                            <span className="inline-flex items-center gap-1">
                                              w tym bez współczynnika (1:1)
                                              <Lock className="w-3 h-3 text-amber-500" />:
                                            </span>
                                          </td>
                                          <td className="text-right font-mono text-gray-700 dark:text-gray-300">
                                            {fmt(exemptSum)}
                                          </td>
                                        </tr>
                                      )}
                                      <tr>
                                        <td colSpan={3} className="pr-3 text-right text-gray-500 dark:text-gray-400 font-medium">
                                          Współczynnik {parseFloat(p.multiplier)}%:
                                        </td>
                                        <td className="text-right font-mono font-bold text-[var(--color-primary)] dark:text-blue-400">
                                          {fmt(Number(p.value))}
                                        </td>
                                      </tr>
                                    </>
                                  );
                                })()}
                              </tfoot>
                            </table>
                          </div>
                        )}

                        {p.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.notes}</p>}
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {p.proposed_by_name} · {new Date(p.created_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
                          {p.reviewed_by_name && (
                            <span className="ml-2">
                              · {p.status === "accepted" ? "Zaakceptował" : "Odrzucił"}: {p.reviewed_by_name}
                            </span>
                          )}
                        </div>
                        {p.notifications.length > 0 && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            Powiadomiono: {p.notifications.map((n) => n.recipient_name).join(", ")}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-1 flex-wrap justify-end flex-shrink-0">
                        {canCalculatePricing && p.status === "pending" && (
                          <>
                            <button
                              onClick={() => openEditPricing(p)}
                              className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-600"
                              title="Edytuj wycenę"
                            >
                              <Pencil size={13} />
                            </button>
                            <Button size="sm" variant="secondary" onClick={() => openNotifyModal(p.id)}>
                              <Send size={12} /> Powiadom
                            </Button>
                          </>
                        )}
                        {canApprovePricing && p.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => reviewProposal.mutate({ roundId: round.id, proposalId: p.id, action: "accept" })}
                              loading={reviewProposal.isPending}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle size={12} /> Akceptuj
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => reviewProposal.mutate({ roundId: round.id, proposalId: p.id, action: "reject" })}
                              loading={reviewProposal.isPending}
                            >
                              <XCircle size={12} /> Odrzuć
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Submit Offer Modal */}
      <Modal open={offerModal} onClose={() => setOfferModal(false)} title={`Oznacz jako złożona w przetargu — Tura ${round.round_number}`} size="sm">
        <form onSubmit={handleSubmitOffer} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Wartość oferty netto (PLN) <span className="text-red-500">*</span>
            </label>
            <input
              type="number" step="0.01" min="0"
              value={offerValue} onChange={(e) => setOfferValue(e.target.value)} required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Uwagi</label>
            <textarea value={offerNotes} onChange={(e) => setOfferNotes(e.target.value)} rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plik oferty (opcjonalnie)</label>
            <FileDropZone
              files={offerFile ? [offerFile] : []}
              onFilesChange={(fs) => setOfferFile(fs[0] ?? null)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setOfferModal(false)}>Anuluj</Button>
            <Button type="submit" size="sm" loading={submitOffer.isPending} disabled={!offerValue}>Zapisz</Button>
          </div>
        </form>
      </Modal>

      {/* Pricing Proposal Modal */}
      <Modal
        open={pricingModal}
        onClose={() => setPricingModal(false)}
        title={editingProposalId ? `Edytuj wycenę — Tura ${round.round_number}` : `Propozycja wyceny — Tura ${round.round_number}`}
        size="lg"
      >
        <form onSubmit={handleSavePricing} className="space-y-5">
          {/* ── Mode switcher ── */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg w-fit">
            {(
              [
                { id: "preset", icon: <BookTemplate size={13} />, label: "Preset" },
                { id: "catalog", icon: <List size={13} />, label: "Z katalogu" },
                { id: "manual", icon: <Edit size={13} />, label: "Ręcznie" },
              ] as { id: CatalogMode; icon: React.ReactNode; label: string }[]
            ).map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setCatalogMode(m.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  catalogMode === m.id
                    ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {m.icon} {m.label}
              </button>
            ))}
          </div>

          {/* ── Preset panel ── */}
          {catalogMode === "preset" && (
            <div className="border border-blue-100 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 space-y-3">

              {/* ZE drawing import — only visible for ZE tenders with drawn variants */}
              {hasZE && (
                <div className="border border-[var(--color-primary)]/30 bg-white dark:bg-gray-800 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-[var(--color-primary)] flex items-center gap-1.5">
                    <BookTemplate size={12} />
                    Importuj parametry z rysunku ZE
                  </p>
                  <div className="flex items-center gap-2">
                    {zeVariants!.length > 1 ? (
                      <select
                        value={zeImportVariantId}
                        onChange={(e) => {
                          setZeImportVariantId(e.target.value);
                          setZeImport(null);
                        }}
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                      >
                        {zeVariants!.map((v) => (
                          <option key={v.id} value={v.id}>Wariant {v.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="flex-1 text-xs text-gray-600 dark:text-gray-400">
                        Wariant {zeVariants![0].label}
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={zeImportLoading}
                      onClick={() => loadZEParams(zeImportVariantId || zeVariants![0].id)}
                      className="shrink-0 px-3 py-1.5 rounded text-xs font-medium bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
                    >
                      {zeImportLoading ? "Wczytuję…" : "Wczytaj"}
                    </button>
                  </div>

                  {zeImportError && (
                    <p className="text-[11px] text-red-600">{zeImportError}</p>
                  )}

                  {zeImport && zeImportParams && (() => {
                    // "Nothing drawn" is a statement about the drawing, not about the
                    // numbers: a route full of elements can legitimately count zero
                    // everywhere, and saying "rysunek nie zawiera elementów" there sent
                    // the author looking for a missing drawing instead of a missing sweep.
                    if (zeImport.lines.length + zeImport.objects.length === 0)
                      return <p className="text-[11px] text-gray-400">Rysunek nie zawiera narysowanych elementów.</p>;
                    if (Object.keys(zeImportParams).length === 0)
                      return <p className="text-[11px] text-gray-400">Narysowane elementy nie mają przypisanych kluczy presetu.</p>;

                    const preset = allPresets.find((p: PricingPreset) => p.id === selectedPresetId);
                    const presetKeys = new Set(preset?.parameters.map((p) => p.key) ?? []);
                    const untrusted = oscUntrusted(zeImport.road_neighbors);
                    const rows = buildImportRows(zeImportParams, presetKeys, untrusted);
                    const { matchCount, zeroMatchCount } = summarizeImport(rows);

                    return (
                      <div className="space-y-1.5">
                        <div className="max-h-40 overflow-y-auto space-y-0.5">
                          {rows.map((row) => (
                            <div
                              key={row.key}
                              className={`flex items-center justify-between text-[11px] font-mono${row.isZero ? " opacity-60" : ""}`}
                              title={row.untrusted ? "Sąsiedzi dróg wewnętrznych nie są policzeni — ta wartość może być zaniżona." : undefined}
                            >
                              <span className={row.matches ? "text-[var(--color-primary)]" : "text-gray-400 dark:text-gray-500"}>
                                {row.untrusted && <span className="text-amber-500 mr-1">●</span>}
                                {row.key}
                              </span>
                              <span className={`ml-2 ${row.isZero ? "text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-300"}`}>
                                {row.value}
                              </span>
                            </div>
                          ))}
                        </div>
                        {preset && matchCount > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setPresetParamValues({ ...presetParamValues, ...pickInsertable(rows) })
                            }
                            className="w-full py-1 rounded text-[11px] font-medium border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)]/5"
                          >
                            Wstaw {matchCount} pasujące parametry do presetu
                            {zeroMatchCount > 0 && ` (w tym ${zeroMatchCount} zerowych)`}
                          </button>
                        )}
                        {(!preset || matchCount === 0) && (
                          <p className="text-[11px] text-gray-400">
                            {!preset ? "Wybierz preset, aby dopasować parametry." : "Brak pasujących parametrów w wybranym presecie."}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Trust signal for ilosc_dz_prv_osc_dr_wewn*: marked internal roads whose
                      neighbours were never discovered make those keys read 0 for a reason
                      that has nothing to do with reality. Same wording and button as the
                      parcels panel, so the two screens agree. */}
                  {zeImport && oscUntrusted(zeImport.road_neighbors) && (
                    <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2 space-y-1.5">
                      <p className="text-[11px] text-amber-700 dark:text-amber-500">
                        Sąsiedzi dróg wewnętrznych nie są policzeni dla:{" "}
                        {zeImport.road_neighbors!.roads_missing.join(", ")} — ilości działek
                        ościennych (<span className="font-mono">ilosc_dz_prv_osc_dr_wewn</span>,{" "}
                        <span className="font-mono">ilosc_dz_prv_osc_dr_wewn_poza</span>) są
                        zaniżone. Przelicz je przed wyceną.
                      </p>
                      {onRecomputeRoadNeighbors && (
                        <button
                          type="button"
                          disabled={!!roadNeighborsBusy}
                          onClick={() =>
                            void onRecomputeRoadNeighbors(zeImportVariantId || zeVariants![0].id)
                          }
                          className="w-full py-1 rounded text-[11px] font-medium border border-amber-500 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-60"
                        >
                          {roadNeighborsBusy ? "Obliczanie…" : "Przelicz sąsiadów dróg"}
                        </button>
                      )}
                    </div>
                  )}

                  {/* MPZP coverage + gap warnings — mirror the "Działki pod trasą" panel so
                      the pricing author always sees the coverage % after importing the drawing,
                      with confirmed gaps / undeterminable stretches flagged below it. */}
                  {zeImportParams && (() => {
                    const selVar = zeVariants!.find(
                      (v) => v.id === (zeImportVariantId || zeVariants![0].id)
                    );
                    if (!selVar || selVar.mpzpComputedAt == null) return null;
                    const hasGap = !!selVar.hasMpzpGap;
                    const unknownPct = selVar.mpzpUnknownPct ?? 0;
                    const hasProtected = !!selVar.hasProtectedAreas;
                    // Coverage % always shows; the gap / "no data" warnings only when relevant.
                    // Bail only if there is genuinely nothing to display.
                    if (selVar.mpzpCoveragePct == null && !hasGap && unknownPct <= 0 && !hasProtected) return null;

                    return (
                      <div className="border-t border-gray-100 dark:border-gray-700 pt-2 space-y-1.5">
                        {selVar.mpzpCoveragePct != null && (
                          <div className="flex items-center justify-between text-[11px] font-mono">
                            <span className="text-gray-500 dark:text-gray-400">Pokrycie MPZP</span>
                            <span className="font-bold text-gray-700 dark:text-gray-300">
                              {selVar.mpzpCoveragePct.toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {hasGap && (
                          <div className="flex items-center gap-1.5 px-2.5 py-2 bg-red-600 text-white font-mono font-bold text-[11px] tracking-wide">
                            <span className="text-base leading-none">⚠</span>
                            BRAKI W MPZP — KONIECZNE ULICP
                          </div>
                        )}
                        {unknownPct > 0 && (
                          <div className="flex items-center gap-1.5 px-2.5 py-2 bg-gray-500 text-white font-mono font-bold text-[11px] tracking-wide">
                            <span className="text-base leading-none">?</span>
                            BRAK DANYCH MPZP — NIE MOŻNA USTALIĆ ({unknownPct.toFixed(1)}%)
                          </div>
                        )}
                        {hasProtected && (
                          <div className="flex items-center gap-1.5 px-2.5 py-2 bg-red-600 text-white font-mono font-bold text-[11px] tracking-wide">
                            <span className="text-base leading-none">⚠</span>
                            OBSZARY CHRONIONE NA TRASIE (GDOŚ)
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Wybierz preset wyceny
                </label>
                <select
                  value={selectedPresetId}
                  onChange={(e) => {
                    setSelectedPresetId(e.target.value);
                    // Pre-fill parameter defaults
                    const preset = allPresets.find((p: PricingPreset) => p.id === e.target.value);
                    if (preset) {
                      const defaults: Record<string, string> = {};
                      preset.parameters.forEach((param) => {
                        defaults[param.key] = param.default_value ?? "";
                      });
                      setPresetParamValues(defaults);
                    }
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                >
                  <option value="">— Wybierz preset —</option>
                  {allPresets.map((p: PricingPreset) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.client_name ? ` (${p.client_name})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {selectedPresetId && (() => {
                const preset = allPresets.find((p: PricingPreset) => p.id === selectedPresetId);
                if (!preset || preset.parameters.length === 0) return null;
                return (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Parametry:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {preset.parameters.map((param) => {
                        const fromDrawing = zeImportParams && param.key in zeImportParams;
                        return (
                          <div key={param.id}>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-0.5">
                              {param.label}{param.unit ? ` (${param.unit})` : ""}
                              <span className="ml-1 font-mono text-[10px] text-gray-400 dark:text-gray-500">{param.key}</span>
                              {fromDrawing && (
                                <span className="ml-1 text-[10px] text-[var(--color-primary)]">↔ rysunek</span>
                              )}
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={presetParamValues[param.key] ?? ""}
                              onChange={(e) => handlePresetParamChange(param.key, e.target.value)}
                              className={`w-full border rounded px-2 py-1 text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] ${
                                fromDrawing
                                  ? "border-[var(--color-primary)]/40 dark:border-[var(--color-primary)]/30"
                                  : "border-gray-300 dark:border-gray-600"
                              }`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <Button
                type="button"
                size="sm"
                disabled={!selectedPresetId}
                loading={resolvePreset.isPending}
                onClick={handleApplyPreset}
              >
                <BookTemplate size={13} /> Zastosuj preset
              </Button>

              {/* Catalog picker also available in preset mode */}
              <div className="border-t border-blue-100 dark:border-blue-800 pt-3 space-y-2">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Dodaj pozycje z katalogu:</p>
                <input
                  type="text"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  placeholder="Filtruj po nazwie..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
                <div className="max-h-36 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {allTemplates
                    .filter((t: PricingItemTemplate) => !catalogSearch.trim() || t.name.toLowerCase().includes(catalogSearch.toLowerCase()))
                    .map((t: PricingItemTemplate) => (
                      <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                        <input type="checkbox" checked={catalogSelected.has(t.id)} onChange={() => toggleCatalogItem(t.id)} className="rounded border-gray-300" />
                        <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 min-w-0 truncate">{t.name}</span>
                        {!t.apply_multiplier && (
                          <span title="Nie podlega współczynnikowi mnożnikowemu — liczona 1:1" className="cursor-help text-amber-500 flex-shrink-0">
                            <Lock size={11} />
                          </span>
                        )}
                        <span className="text-xs text-gray-400 font-mono flex-shrink-0">{fmt(Number(t.base_rate))} PLN/{t.unit}</span>
                      </label>
                    ))}
                </div>
                {catalogSelected.size > 0 && (
                  <Button type="button" size="sm" variant="secondary" onClick={handleAddFromCatalog}>
                    <List size={13} /> Dodaj wybrane ({catalogSelected.size})
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* ── Catalog panel ── */}
          {catalogMode === "catalog" && (
            <div className="rounded-xl p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Wyszukaj pozycje katalogowe
                </label>
                <input
                  type="text"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  placeholder="Filtruj po nazwie..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              </div>

              <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                {allTemplates
                  .filter((t: PricingItemTemplate) =>
                    !catalogSearch.trim() ||
                    t.name.toLowerCase().includes(catalogSearch.toLowerCase())
                  )
                  .map((t: PricingItemTemplate) => (
                    <label
                      key={t.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={catalogSelected.has(t.id)}
                        onChange={() => toggleCatalogItem(t.id)}
                        className="rounded border-gray-300"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-700 dark:text-gray-300">{t.name}</span>
                        {t.notes && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 truncate">{t.notes}</span>
                        )}
                      </div>
                      {!t.apply_multiplier && (
                        <span title="Nie podlega współczynnikowi mnożnikowemu — liczona 1:1" className="cursor-help text-amber-500 flex-shrink-0">
                          <Lock size={12} />
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono flex-shrink-0">
                        {Number(t.base_rate).toLocaleString("pl-PL", { minimumFractionDigits: 2, useGrouping: "always" })} PLN/{t.unit}
                      </span>
                    </label>
                  ))}
                {allTemplates.filter((t: PricingItemTemplate) =>
                  !catalogSearch.trim() || t.name.toLowerCase().includes(catalogSearch.toLowerCase())
                ).length === 0 && (
                  <p className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                    Brak wyników.
                  </p>
                )}
              </div>

              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={catalogSelected.size === 0}
                onClick={handleAddFromCatalog}
              >
                <List size={13} /> Dodaj wybrane ({catalogSelected.size})
              </Button>
            </div>
          )}

          {/* Line items table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Pozycje wyceny</label>
              <button
                type="button"
                onClick={() => setPricingItems((prev) => [...prev, emptyItem()])}
                className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] flex items-center gap-1"
              >
                <Plus size={12} /> Dodaj pozycję
              </button>
            </div>

            <div className="rounded-lg overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[1fr_140px_100px_100px_56px] gap-0 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                <span>Pozycja (co)</span>
                <span className="text-right">Stawka bazowa</span>
                <span className="text-right">Ilość</span>
                <span className="text-right">Razem</span>
                <span />
              </div>

              {/* Rows */}
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {[...pricingItems].sort(byZeroQuantityLast).map((item) => {
                  const rate = parseFloat(item.base_rate.replace(",", ".")) || 0;
                  const qty = parseFloat(item.quantity.replace(",", ".")) || 0;
                  const lineTotal = rate * qty;
                  const isZero = isZeroQuantity(item.quantity);
                  return (
                    <div key={item.key} className={`grid grid-cols-[1fr_140px_100px_100px_56px] gap-0 items-center px-3 py-1.5${isZero ? " opacity-50" : ""}`}>
                      <input
                        value={item.name}
                        onChange={(e) => updateItem(item.key, "name", e.target.value)}
                        placeholder="Opis pozycji..."
                        className="pr-2 text-sm bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
                      />
                      <input
                        value={item.base_rate}
                        onChange={(e) => updateItem(item.key, "base_rate", e.target.value)}
                        placeholder="0.00"
                        className="pl-2 text-right text-sm font-mono bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none border-l border-gray-200 dark:border-gray-600"
                      />
                      <input
                        value={item.quantity}
                        onChange={(e) => updateItem(item.key, "quantity", e.target.value)}
                        placeholder="1"
                        className="pl-2 text-right text-sm font-mono bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none border-l border-gray-200 dark:border-gray-600"
                      />
                      <span className="pl-2 text-right text-sm font-mono text-gray-700 dark:text-gray-300 border-l border-gray-200 dark:border-gray-600">
                        {lineTotal > 0 ? fmt(lineTotal) : "—"}
                      </span>
                      <div className="ml-1 flex items-center justify-end gap-0.5">
                        <button
                          type="button"
                          onClick={() => toggleItemMultiplier(item.key)}
                          title={
                            item.apply_multiplier
                              ? "Podlega współczynnikowi mnożnikowemu — kliknij, aby liczyć 1:1"
                              : "Nie podlega współczynnikowi mnożnikowemu (liczona 1:1) — kliknij, aby przywrócić"
                          }
                          className={`p-1 ${
                            item.apply_multiplier
                              ? "text-gray-300 hover:text-amber-500"
                              : "text-amber-500 hover:text-gray-400"
                          }`}
                        >
                          <Lock size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(item.key)}
                          disabled={pricingItems.length === 1}
                          className="p-1 text-gray-300 hover:text-red-500 disabled:opacity-20 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Subtotal row */}
              <div className="grid grid-cols-[1fr_140px_100px_100px_56px] bg-gray-50 dark:bg-gray-700/30 px-3 py-2 border-t border-gray-200 dark:border-gray-600">
                <span className="text-xs text-gray-500 dark:text-gray-400 col-span-3 text-right pr-2">Suma bazowa:</span>
                <span className="pl-2 text-right text-sm font-mono font-semibold text-gray-900 dark:text-gray-100 border-l border-gray-200 dark:border-gray-600">
                  {fmt(subtotal)}
                </span>
                <span />
              </div>
            </div>
          </div>

          {/* Multiplier slider */}
          <div className="rounded-lg p-3 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-auto">
                Współczynnik mnożnikowy
              </label>
              {/* Quick presets */}
              <div className="flex items-center gap-1">
                {[75, 90, 100, 110, 125].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setPricingMultiplier(v)}
                    className={`px-2 py-0.5 rounded text-xs font-mono transition-colors ${
                      pricingMultiplier === v
                        ? "bg-[var(--color-primary)] text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    {v}%
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="range"
                min={5}
                max={200}
                step={0.5}
                value={pricingMultiplier}
                onChange={(e) => setPricingMultiplier(Number(e.target.value))}
                className="flex-1 h-2 rounded-full cursor-pointer accent-[var(--color-primary)]"
                style={{
                  background: `linear-gradient(to right, #2563eb ${Math.min(100, ((pricingMultiplier - 5) / 195) * 100)}%, #e5e7eb ${Math.min(100, ((pricingMultiplier - 5) / 195) * 100)}%)`,
                }}
              />
              <input
                type="number"
                min={5}
                step="0.01"
                value={multiplierDraft ?? pricingMultiplier}
                onChange={(e) => {
                  setMultiplierDraft(e.target.value);
                  const v = Number(e.target.value);
                  if (e.target.value !== "" && Number.isFinite(v) && v >= 5) {
                    setPricingMultiplier(v);
                  }
                }}
                onBlur={() => {
                  const v = Number(multiplierDraft ?? pricingMultiplier);
                  setPricingMultiplier(Number.isFinite(v) ? Math.max(5, v) : 5);
                  setMultiplierDraft(null);
                }}
                className="w-20 text-center border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm font-mono font-semibold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
            </div>

            {/* Visual result */}
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
              <span className="font-mono">{fmt(multipliedSubtotal)}</span>
              <span>×</span>
              <span className={`font-semibold font-mono ${pricingMultiplier < 100 ? "text-orange-500" : pricingMultiplier > 100 ? "text-green-600 dark:text-green-400" : "text-gray-500"}`}>
                {pricingMultiplier}%
              </span>
              {exemptSubtotal > 0 && (
                <>
                  <span>+</span>
                  <span
                    className="inline-flex items-center gap-1 font-mono"
                    title="Pozycje niepodlegające współczynnikowi mnożnikowemu (liczone 1:1)"
                  >
                    {fmt(exemptSubtotal)}
                    <Lock size={11} className="text-amber-500" />
                  </span>
                </>
              )}
              <span>=</span>
              <span className="font-semibold font-mono text-gray-900 dark:text-gray-100">{fmt(total)}</span>
            </div>
          </div>

          {/* Total */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <span className="text-sm font-medium text-[var(--color-primary)] dark:text-blue-300">
              Łączna wartość propozycji
              {pricingMultiplier !== 100 && (
                <span className="ml-1 font-normal text-[var(--color-primary)]">
                  ({fmt(multipliedSubtotal)} × {pricingMultiplier}%
                  {exemptSubtotal > 0 ? ` + ${fmt(exemptSubtotal)}` : ""})
                </span>
              )}
            </span>
            <span className="text-xl font-bold font-mono text-[var(--color-primary)] dark:text-blue-300 self-end sm:self-auto">
              {fmt(total)} PLN
            </span>
          </div>

          {/* Expected win value + margin — only for users who may APPROVE pricing */}
          {canApprovePricing && (
            <PricingHint
              tenderId={tenderId}
              templateIds={pricingItems
                .map((i) => i.template_id)
                .filter((id): id is string => Boolean(id))}
              currentMultiplier={pricingMultiplier}
              baseSubtotal={multipliedSubtotal}
              onApply={setPricingMultiplier}
            />
          )}

          {/* Similar proposals panel */}
          <SimilarProposals
            tenderId={tenderId}
            templateIds={pricingItems
              .map((i) => i.template_id)
              .filter((id): id is string => Boolean(id))}
          />

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Uwagi do propozycji</label>
            <textarea
              value={pricingNotes}
              onChange={(e) => setPricingNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
              placeholder="Podstawa wyceny, założenia..."
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" className="w-full sm:w-auto justify-center" onClick={() => setPricingModal(false)}>Anuluj</Button>
            <Button type="submit" size="sm" className="w-full sm:w-auto justify-center" loading={isPricingPending} disabled={!canSavePricing}>
              {editingProposalId ? "Zapisz zmiany" : "Dodaj propozycję"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Copy Picker Modal */}
      <Modal open={copyPickerModal} onClose={() => setCopyPickerModal(false)} title="Wybierz wariant do skopiowania" size="sm">
        <div className="space-y-2">
          {round.pricing_proposals.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => openCopyFromProposal(p)}
              className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-[var(--color-primary)] hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                  {fmt(Number(p.value))} PLN
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {p.proposed_by_name} · {new Date(p.created_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
                  {p.items.length > 0 && ` · ${p.items.length} poz.`}
                </div>
                {p.notes && (
                  <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{p.notes}</div>
                )}
              </div>
              <Badge color={PROPOSAL_STATUS_COLORS[p.status]} className="ml-3 flex-shrink-0">
                {PRICING_PROPOSAL_STATUS_LABELS[p.status as keyof typeof PRICING_PROPOSAL_STATUS_LABELS]}
              </Badge>
            </button>
          ))}
        </div>
      </Modal>

      {/* Notify Modal */}
      <Modal open={notifyModal} onClose={() => setNotifyModal(false)} title="Powiadom o propozycji wyceny" size="md">
        <form onSubmit={handleSendNotification} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Wybierz odbiorców <span className="text-red-500">*</span>
            </label>
            <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {allUsers.map((u) => (
                <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifyRecipients.includes(u.id)}
                    onChange={() => toggleRecipient(u.id)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{u.first_name} {u.last_name}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Wiadomość (opcjonalnie)</label>
            <textarea
              value={notifyMessage} onChange={(e) => setNotifyMessage(e.target.value)} rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
              placeholder="Proszę o zapoznanie się z propozycją wyceny i akceptację..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setNotifyModal(false)}>Anuluj</Button>
            <Button type="submit" size="sm" loading={sendNotification.isPending} disabled={notifyRecipients.length === 0}>
              <Send size={13} /> Wyślij powiadomienie
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
